import type { CatchDecision, CatchResult, DecisionOutcome, RoomKind, Tackle } from "./game";

export interface Profile { id: string; name: string; authenticated?: boolean }
export interface ScoreRow { name: string; score: number; weightKg: number; catches: number }
export interface Activity { casts: number; catches: number; totalKg: number; online: number }

async function json<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
  });
  if (!response.ok) throw new Error((await response.text()) || `Request failed (${response.status})`);
  return response.json() as Promise<T>;
}

export async function getIdentity(): Promise<Profile | null> {
  try {
    const result = await json<{ clientPrincipal?: { userId: string; userDetails: string } }>("/.auth/me");
    if (!result.clientPrincipal) return null;
    return { id: result.clientPrincipal.userId, name: result.clientPrincipal.userDetails, authenticated: true };
  } catch { return null; }
}

export async function negotiate(room: RoomKind, profile: Profile, accessCode = "") {
  return json<{ url: string }>("/api/negotiate", { method: "POST", body: JSON.stringify({ room, profile, accessCode }) });
}

export async function startCast(room: RoomKind, profile: Profile, tackle: Tackle, accessCode = "") {
  return json<CatchResult>("/api/cast", { method: "POST", body: JSON.stringify({ room, profile, tackle, accessCode }) });
}

export async function reelCatch(room: RoomKind, profile: Profile, result: CatchResult, reactionMs: number, accessCode = "") {
  return json<{ token: string }>("/api/reel", { method: "POST", body: JSON.stringify({ room, profile, token: result.token, reactionMs, accessCode }) });
}

export async function landCatch(room: RoomKind, profile: Profile, result: CatchResult, decision: CatchDecision, accessCode = "") {
  return json<DecisionOutcome>("/api/catch", {
    method: "POST",
    body: JSON.stringify({ room, profile, token: result.token, decision, accessCode }),
  });
}

export async function getPlayerState(room: RoomKind, profile: Profile, accessCode = "") {
  return json<{ keptKg: number }>("/api/player-state", { method: "POST", body: JSON.stringify({ room, profile, accessCode }) });
}

export async function getStats(room: RoomKind) {
  return json<{ leaderboard: ScoreRow[]; activity: Activity }>(`/api/stats?room=${room}`);
}
